/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  keepPreviousData,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type CreateInvoicePayload,
  type CreditNote,
  type FinanceOverview,
  type Invoice,
  type InvoicesPageParams,
  type IssueCreditNotePayload,
  type Page,
  type Payment,
  type RecordPaymentPayload,
  type ServiceItem,
  type ServiceItemPayload,
  catalogueApi,
  financeApi,
} from '../api/finance.js';

const INVOICES_KEY = ['finance', 'invoices'] as const;
const OVERVIEW_KEY = ['finance', 'overview'] as const;

/** Finance Overview metrics; polls every 60s for near-real-time figures. */
export function useFinanceOverview(): UseQueryResult<FinanceOverview> {
  return useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => financeApi.overview(),
    refetchInterval: 60_000,
  });
}

/** Send an outstanding-invoice reminder email (Admin/Finance). */
export function useRemindInvoice(): UseMutationResult<
  { sent: boolean; email: string | null },
  Error,
  string
> {
  return useMutation({ mutationFn: (id: string) => financeApi.remind(id) });
}
const paymentsKey = (id: string): readonly unknown[] => ['finance', 'invoices', id, 'payments'];
const catalogueKey = (activeOnly: boolean): readonly unknown[] => [
  'finance',
  'service-items',
  activeOnly,
];

export function useInvoices(): UseQueryResult<readonly Invoice[]> {
  return useQuery({ queryKey: INVOICES_KEY, queryFn: () => financeApi.list() });
}

/** Paginated, searchable invoice list for the role-scoped management table (P1-1). */
export function useInvoicesPage(params: InvoicesPageParams): UseQueryResult<Page<Invoice>> {
  return useQuery({
    queryKey: ['finance', 'invoices', 'page', params],
    queryFn: () => financeApi.listPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useInvoicePayments(id: string): UseQueryResult<readonly Payment[]> {
  return useQuery({ queryKey: paymentsKey(id), queryFn: () => financeApi.payments(id) });
}

export function useCreateInvoice(): UseMutationResult<Invoice, Error, CreateInvoicePayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInvoicePayload) => financeApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
  });
}

interface RecordPaymentVars {
  readonly id: string;
  readonly payload: RecordPaymentPayload;
}

export function useRecordPayment(): UseMutationResult<Invoice, Error, RecordPaymentVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: RecordPaymentVars) => financeApi.recordPayment(vars.id, vars.payload),
    onSuccess: (_invoice, vars) => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: paymentsKey(vars.id) });
    },
  });
}

const creditNotesKey = (id: string): readonly unknown[] => [
  'finance',
  'invoices',
  id,
  'credit-notes',
];

export function useCreditNotes(id: string): UseQueryResult<readonly CreditNote[]> {
  return useQuery({ queryKey: creditNotesKey(id), queryFn: () => financeApi.creditNotes(id) });
}

interface IssueCreditNoteVars {
  readonly id: string;
  readonly payload: IssueCreditNotePayload;
}

export function useIssueCreditNote(): UseMutationResult<CreditNote, Error, IssueCreditNoteVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: IssueCreditNoteVars) => financeApi.issueCreditNote(vars.id, vars.payload),
    onSuccess: (_note, vars) => {
      void qc.invalidateQueries({ queryKey: creditNotesKey(vars.id) });
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}

/** Parent self-service pay-now (P0-3) — settles the invoice's outstanding balance. */
export function usePayInvoice(): UseMutationResult<Invoice, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.payOwn(id),
    onSuccess: (_invoice, id) => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: paymentsKey(id) });
    },
  });
}

/** The service catalogue (P1-9); pass `activeOnly` for the invoice picker set. */
export function useServiceItems(activeOnly = false): UseQueryResult<readonly ServiceItem[]> {
  return useQuery({
    queryKey: catalogueKey(activeOnly),
    queryFn: () => catalogueApi.list(activeOnly),
  });
}

const CATALOGUE_KEY = ['finance', 'service-items'] as const;

export function useCreateServiceItem(): UseMutationResult<ServiceItem, Error, ServiceItemPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ServiceItemPayload) => catalogueApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOGUE_KEY });
    },
  });
}

interface UpdateServiceItemVars {
  readonly id: string;
  readonly payload: Partial<ServiceItemPayload>;
}

export function useUpdateServiceItem(): UseMutationResult<
  ServiceItem,
  Error,
  UpdateServiceItemVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateServiceItemVars) => catalogueApi.update(vars.id, vars.payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOGUE_KEY });
    },
  });
}

export function useDeleteServiceItem(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogueApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOGUE_KEY });
    },
  });
}
